import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { Star, User, Users, FileText, Building2, ChevronRight, GitBranch } from "lucide-react";
import { STEP_FIELD_ICONS } from "./stepFieldIcons";
import { SUCCESSOR_ICON, SYSTEM_ICON } from "../../../constants/entity-icons";
import { hasDecisionRules, variantRespondentCount } from "@bliks-io/contracts";
import { useTranslation } from "react-i18next";
import { cn } from "../../../utils";
import { formatDateTime } from "../data-table/utils";
import { appendDeletedSuffix, DELETED_ENTITY_CLASS, isAssigneeDeleted, isUserDeletedInLookup } from "../../../utils/deleted-entity-utils";
import { StepDetailPanel, StepDetailHeader, StepDetailBody } from "./StepDetailBuildingBlocks";
import { DecisionRulesSection } from "../decision-table/DecisionRulesSection";
import { SuccessorEntities } from "./SuccessorEntities";
import { lookupAssignee } from "../process-flow/core/builder";
import { useStepSuggestionInfoMap } from "./useStepSuggestionInfoMap";
import { resolveEntityRefDisplay } from "./suggestionNames";
import { ProposedEntityNameLabel } from "./ProposedEntityName";
import { UserName } from "../user-card/user-name";
import { IdentityAvatar } from "../user-card/identity-avatar";
import { Dialog, DialogTrigger } from "../../base/dialog/dialog";
import { DialogShell } from "../dialog-shell/DialogShell";
import { DialogShellBody } from "../dialog-shell/DialogShellBody";
import { DialogShellHeader } from "../dialog-shell/DialogShellHeader";
function InputsList({ inputs, t }) {
    if (!inputs || inputs.length === 0) return null;
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("label", {
                className: "flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1",
                children: [
                    /*#__PURE__*/ _jsx(STEP_FIELD_ICONS.input, {
                        className: "w-3.5 h-3.5"
                    }),
                    t("processFlow.inputs")
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "space-y-1.5",
                children: inputs.map((input, index)=>{
                    // Derived event inputs arrive hydrated from their event node
                    // (resolve-recording-inputs) — the stored fields ARE current.
                    const { name: displayName, description: displayDescription } = input;
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "text-sm text-foreground bg-surface-subtle px-2 py-1.5 rounded-sm",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-medium",
                                children: displayName
                            }),
                            displayDescription && /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-fg-secondary mt-0.5",
                                children: displayDescription
                            })
                        ]
                    }, index);
                })
            })
        ]
    });
}
/**
 * One helper box for a slot of `{ type, id }` union refs (functions, systems):
 * each ref resolves to its entity name via the lookup, or to its proposed name
 * (blue / red once declined) when it is still a suggestion.
 */ function RefHelperGroup({ icon: Icon, title, refs, model, pendingLabel, keyPrefix, lookup, suggestionInfoById, t }) {
    if (refs.length === 0) return null;
    return /*#__PURE__*/ _jsxs("div", {
        className: "bg-surface-subtle rounded-md px-2 py-1.5",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-1 text-xs font-medium text-fg-secondary mb-0.5",
                children: [
                    /*#__PURE__*/ _jsx(Icon, {
                        className: "w-3 h-3"
                    }),
                    title
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "text-xs text-fg-secondary space-y-0.5",
                children: refs.map((ref, i)=>{
                    const row = resolveEntityRefDisplay(ref, {
                        suggestionInfoById,
                        pendingLabel,
                        getEntity: (id)=>lookupAssignee({
                                type: "entity",
                                id
                            }, model, lookup),
                        t
                    });
                    return /*#__PURE__*/ _jsx("div", {
                        className: cn(row.kind === "entity" && row.isDeleted && DELETED_ENTITY_CLASS),
                        children: row.kind === "proposed" ? /*#__PURE__*/ _jsx(ProposedEntityNameLabel, {
                            name: row.label,
                            isDeclined: row.isDeclined
                        }) : row.label
                    }, `${keyPrefix}-${ref.id || i}`);
                })
            })
        ]
    });
}
function HelpersSection({ helpers, t, lookup, suggestionInfoById }) {
    if (!helpers) return null;
    const { persons, orgFunctions, systems, documents } = helpers;
    const hasContent = (persons?.length || 0) + (orgFunctions?.length || 0) + (systems?.length || 0) + (documents?.length || 0) > 0;
    if (!hasContent) return null;
    const pendingSystem = t("processFlow.pendingSystem");
    const pendingOrgFunction = t("processFlow.pendingOrgFunction");
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("label", {
                className: "flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1",
                children: [
                    /*#__PURE__*/ _jsx(STEP_FIELD_ICONS.helpers, {
                        className: "w-3.5 h-3.5"
                    }),
                    t("processFlow.helpers")
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap gap-2",
                children: [
                    persons && persons.length > 0 && /*#__PURE__*/ _jsxs("div", {
                        className: "bg-surface-subtle rounded-md px-2 py-1.5",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-1 text-xs font-medium text-fg-secondary mb-0.5",
                                children: [
                                    /*#__PURE__*/ _jsx(Users, {
                                        className: "w-3 h-3"
                                    }),
                                    t("processFlow.persons")
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-xs text-fg-secondary space-y-0.5",
                                children: persons.map((p, i)=>{
                                    // Backend-populated users never carry `deletedAt` — the
                                    // lookup is the only source for the tombstone.
                                    const isDeleted = isUserDeletedInLookup(p._id, lookup);
                                    return /*#__PURE__*/ _jsx("div", {
                                        className: cn(isDeleted && DELETED_ENTITY_CLASS),
                                        children: /*#__PURE__*/ _jsx(UserName, {
                                            name: `${p.firstName} ${p.lastName}`,
                                            user: lookup?.users?.get(p._id) ?? p
                                        })
                                    }, `person-${p._id || i}`);
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(RefHelperGroup, {
                        icon: Building2,
                        title: t("processFlow.teams"),
                        refs: orgFunctions ?? [],
                        model: "OrgFunction",
                        pendingLabel: pendingOrgFunction,
                        keyPrefix: "org-function",
                        lookup: lookup,
                        suggestionInfoById: suggestionInfoById,
                        t: t
                    }),
                    /*#__PURE__*/ _jsx(RefHelperGroup, {
                        icon: SYSTEM_ICON,
                        title: t("processFlow.systems"),
                        refs: systems ?? [],
                        model: "System",
                        pendingLabel: pendingSystem,
                        keyPrefix: "system",
                        lookup: lookup,
                        suggestionInfoById: suggestionInfoById,
                        t: t
                    }),
                    documents && documents.length > 0 && /*#__PURE__*/ _jsxs("div", {
                        className: "bg-surface-subtle rounded-md px-2 py-1.5",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-1 text-xs font-medium text-fg-secondary mb-0.5",
                                children: [
                                    /*#__PURE__*/ _jsx(FileText, {
                                        className: "w-3 h-3"
                                    }),
                                    t("processFlow.documents")
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-xs text-fg-secondary space-y-0.5",
                                children: documents.map((d, i)=>{
                                    // Artifact toJSON keeps deletedAt, so populated docs carry
                                    // the tombstone themselves — no lookup needed.
                                    const isDeleted = isAssigneeDeleted(d);
                                    return /*#__PURE__*/ _jsx("div", {
                                        className: cn(isDeleted && DELETED_ENTITY_CLASS),
                                        children: appendDeletedSuffix(d.name, isDeleted, t)
                                    }, `doc-${d._id || i}`);
                                })
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
function SuccessorsSection({ successors, t, lookup, suggestionInfoById }) {
    if (!successors) return null;
    const { persons, orgFunctions, systems, externalContacts } = successors;
    const hasEntities = (persons?.length || 0) + (orgFunctions?.length || 0) + (systems?.length || 0) + (externalContacts?.length || 0) > 0;
    const hasStatus = successors.successorStatus === "NONE" || successors.successorStatus === "UNCLEAR";
    if (!hasEntities && !hasStatus) return null;
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("label", {
                className: "flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1",
                children: [
                    /*#__PURE__*/ _jsx(SUCCESSOR_ICON, {
                        className: "w-3.5 h-3.5"
                    }),
                    t("processFlow.successors")
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "bg-surface-subtle rounded-md p-2",
                children: /*#__PURE__*/ _jsx(SuccessorEntities, {
                    successors: successors,
                    t: t,
                    orgFunctionsById: lookup.orgFunctions,
                    systemsById: lookup.systems,
                    usersById: lookup.users,
                    suggestionInfoById: suggestionInfoById
                })
            })
        ]
    });
}
function WhoElseDoesThisSection({ whoElseDoesThis, t, lookup, suggestionInfoById }) {
    // Filter out entries with null entity (broken/orphaned data)
    const validEntries = whoElseDoesThis?.filter((item)=>item.entity != null);
    if (!validEntries || validEntries.length === 0) return null;
    const getStatusClass = (status)=>{
        if (status === "accepted") return "bg-positive-surface-strong text-positive";
        if (status === "declined") return "bg-danger-surface-strong text-danger";
        return "bg-surface-strong text-fg-secondary";
    };
    // whoElseDoesThis only ever holds User or OrgFunction entries on the wire. Users
    // never go through suggestions, so a User row's entity is always a resolved
    // ref. OrgFunction rows are either suggestion refs (pending label) or entity refs
    // (resolved via the lookup).
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("label", {
                className: "flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1",
                children: [
                    /*#__PURE__*/ _jsx(Users, {
                        className: "w-3.5 h-3.5"
                    }),
                    t("processFlow.whoElseDoesThis")
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "space-y-1",
                children: validEntries.map((item, idx)=>{
                    const ref = item.entity;
                    const entityId = ref && ref.type === "entity" ? ref.id : null;
                    const key = entityId ?? `who-else-${idx}`;
                    // Only OrgFunction rows can carry a suggestion ref, so only they get
                    // a pending placeholder; User rows always resolve as entity refs.
                    const row = resolveEntityRefDisplay(ref, {
                        suggestionInfoById,
                        pendingLabel: item.model === "OrgFunction" ? t("processFlow.pendingOrgFunction") : "",
                        getEntity: (id)=>lookupAssignee({
                                type: "entity",
                                id
                            }, item.model, lookup),
                        t
                    });
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "text-sm text-foreground flex items-center justify-between bg-surface-subtle px-2 py-1 rounded-sm",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: cn("flex items-center gap-2", row.kind === "entity" && row.isDeleted && DELETED_ENTITY_CLASS),
                                children: [
                                    item.model === "User" ? /*#__PURE__*/ _jsx(User, {
                                        className: "w-3 h-3 text-fg-subtle"
                                    }) : /*#__PURE__*/ _jsx(Building2, {
                                        className: "w-3 h-3 text-fg-subtle"
                                    }),
                                    row.kind === "proposed" ? /*#__PURE__*/ _jsx(ProposedEntityNameLabel, {
                                        name: row.label,
                                        isDeclined: row.isDeclined
                                    }) : row.kind === "entity" ? /*#__PURE__*/ _jsx(UserName, {
                                        name: row.name,
                                        lifecycle: row.lifecycle
                                    }) : /*#__PURE__*/ _jsx("span", {
                                        children: row.label
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: `text-xs px-1.5 py-0.5 rounded-sm ${getStatusClass(item.status)}`,
                                children: t(`processFlow.status.${item.status}`)
                            })
                        ]
                    }, key);
                })
            })
        ]
    });
}
/**
 * A suggestion-typed `assignedTo` shows its proposed Team/System name (blue, or
 * red once declined) when resolvable from the suggestion set, else a
 * kind-specific pending label. Approval rewrites the ref into an entity ref,
 * after which it resolves to the entity name like any other assignee.
 */ function resolveAssignedToDisplay(step, t, lookup, suggestionInfoById) {
    const ref = step.assignedTo;
    if (!ref) return null;
    return resolveEntityRefDisplay(ref, {
        suggestionInfoById,
        pendingLabel: step.assignedToModel === "OrgFunction" ? t("processFlow.pendingOrgFunction") : t("processFlow.pendingSystem"),
        getEntity: (id)=>lookupAssignee({
                type: "entity",
                id
            }, step.assignedToModel, lookup),
        t
    });
}
function SurveyFeedbackSection({ surveys, t }) {
    const validSurveys = (surveys || []).filter((s)=>s.rating);
    if (validSurveys.length === 0) return null;
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("label", {
                className: "flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2",
                children: [
                    /*#__PURE__*/ _jsx(Star, {
                        className: "w-3.5 h-3.5"
                    }),
                    t("processFlow.feedback")
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "space-y-3",
                children: validSurveys.map((survey, index)=>/*#__PURE__*/ _jsxs("div", {
                        className: "border-l-2 border-warning-solid pl-3",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex items-center gap-1.5 mb-1",
                                children: [
                                    1,
                                    2,
                                    3,
                                    4,
                                    5
                                ].map((star)=>/*#__PURE__*/ _jsx(Star, {
                                        className: `w-4 h-4 ${star <= (survey.rating?.stars || 0) ? "text-warning fill-warning-solid" : "text-fg-disabled"}`
                                    }, star))
                            }),
                            survey.rating?.comment && /*#__PURE__*/ _jsxs("p", {
                                className: "text-sm text-fg-secondary mt-1 italic",
                                children: [
                                    '"',
                                    survey.rating.comment,
                                    '"'
                                ]
                            })
                        ]
                    }, index))
            })
        ]
    });
}
const VARIANT_TILES = [
    {
        key: "same",
        tileClass: "bg-positive-surface",
        countClass: "text-positive"
    },
    {
        key: "different",
        tileClass: "bg-warning-surface",
        countClass: "text-warning"
    },
    {
        key: "notPerformed",
        tileClass: "bg-muted",
        countClass: "text-fg-secondary"
    }
];
/** The three count tiles, rendered both in the sidebar and atop the modal body. */ function VariantCountTiles({ variantCheck, t }) {
    return /*#__PURE__*/ _jsx("div", {
        className: "grid grid-cols-3 gap-1.5",
        children: VARIANT_TILES.map(({ key, tileClass, countClass })=>/*#__PURE__*/ _jsxs("div", {
                className: cn("rounded-md px-2 py-1.5 text-center", tileClass),
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: cn("text-lg font-semibold leading-tight tabular-nums", countClass),
                        children: variantCheck[key]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "text-2xs text-fg-secondary",
                        children: t(`processFlow.variantCheck.${key}`)
                    })
                ]
            }, key))
    });
}
function VariantAuthorStack({ variants }) {
    return /*#__PURE__*/ _jsx("span", {
        className: "flex -space-x-1.5",
        children: variants.slice(0, 4).map((variant, index)=>/*#__PURE__*/ _jsx(IdentityAvatar, {
                identity: variant.author,
                className: "ring-2 ring-white"
            }, index))
    });
}
function VariantDescriptionCard({ description, t }) {
    const { author, text } = description;
    const isDeleted = isAssigneeDeleted(author);
    return /*#__PURE__*/ _jsxs("div", {
        className: cn("rounded-md bg-surface-subtle p-2.5", isDeleted && DELETED_ENTITY_CLASS),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 mb-1",
                children: [
                    /*#__PURE__*/ _jsx(IdentityAvatar, {
                        identity: author
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-sm font-medium text-foreground",
                        children: author ? /*#__PURE__*/ _jsx(UserName, {
                            name: author.name,
                            user: author
                        }) : t("viralRecording.labels.unknownUser")
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-fg-secondary",
                children: text
            })
        ]
    });
}
/**
 * The Variant Check (ADR 0022, plan 029), read off the step's own
 * `variantCheck` block rather than off its surveys: the answer is an entity of
 * its own, so it counts the moment it is given — a co-performer who told us
 * they do the step differently and then never finished the classic questions is
 * visible here, and a tenant with every classic question switched off still has
 * this section. That is why it does not live inside
 * {@link SurveyFeedbackSection}.
 *
 * THREE COUNTS OVER ONE DENOMINATOR (D12): the three tiles always render
 * together, decliners included, so the counts visibly sum to everyone who
 * answered the Variant Check. A decline is an answer to the question that was
 * asked, not an absence of one, so it belongs in the same set as the verdicts
 * it is weighed against.
 *
 * The COUNTS are anonymous — the backend ships no identity with them. The
 * `different` descriptions are attributed to their author (ADR 0019 amendment)
 * and live behind the "view variants" modal, never inline; the author avatars
 * on the modal trigger are the one inline attribution surface, and they exist
 * only where the descriptions themselves are one click away. The decline is a
 * bare count; the surfaces that name decliners are the ones a manager acts on
 * (the recording dashboard and the draft editor).
 *
 * The dialog is CONTROLLED on purpose: the package `Dialog` only registers with
 * the ModalStackProvider when it is handed a truthy `open`.
 */ function VariantCheckSection({ variantCheck, t }) {
    const [modalOpen, setModalOpen] = useState(false);
    const total = variantRespondentCount(variantCheck);
    if (total === 0) return null;
    // Shorter than `different`: a legacy answer migrated without a usable text is
    // counted but has nothing to list. Withheld (key absent, on the export/
    // analyzer projections) and "nobody wrote anything" both render the empty
    // state — this surface has nothing different to say about the two.
    const variants = variantCheck.variants ?? [];
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("label", {
                className: "flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2",
                children: [
                    /*#__PURE__*/ _jsx(GitBranch, {
                        className: "w-3.5 h-3.5"
                    }),
                    t("processFlow.variantCheck.title")
                ]
            }),
            /*#__PURE__*/ _jsx(VariantCountTiles, {
                variantCheck: variantCheck,
                t: t
            }),
            variantCheck.different > 0 && /*#__PURE__*/ _jsxs(Dialog, {
                open: modalOpen,
                onOpenChange: setModalOpen,
                children: [
                    /*#__PURE__*/ _jsx(DialogTrigger, {
                        asChild: true,
                        children: /*#__PURE__*/ _jsxs("button", {
                            type: "button",
                            className: "mt-1.5 flex w-full items-center justify-between rounded-md bg-surface-subtle px-2 py-1.5 hover:bg-muted",
                            children: [
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "flex items-center gap-2 text-sm text-foreground",
                                    children: [
                                        /*#__PURE__*/ _jsx(VariantAuthorStack, {
                                            variants: variants
                                        }),
                                        t("processFlow.variantCheck.viewVariants")
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(ChevronRight, {
                                    className: "w-3.5 h-3.5 text-fg-subtle"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs(DialogShell, {
                        size: "sm",
                        children: [
                            /*#__PURE__*/ _jsx(DialogShellHeader, {
                                title: t("processFlow.variantCheck.title"),
                                onClose: ()=>setModalOpen(false)
                            }),
                            /*#__PURE__*/ _jsxs(DialogShellBody, {
                                children: [
                                    /*#__PURE__*/ _jsx(VariantCountTiles, {
                                        variantCheck: variantCheck,
                                        t: t
                                    }),
                                    variants.length > 0 ? /*#__PURE__*/ _jsx("div", {
                                        className: "space-y-2",
                                        children: variants.map((description, index)=>/*#__PURE__*/ _jsx(VariantDescriptionCard, {
                                                description: description,
                                                t: t
                                            }, index))
                                    }) : /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm text-muted-foreground",
                                        children: t("common.noItemsFound")
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
/** Step detail content body — reusable without the sidebar wrapper/header */ export function ProcessFlowStepDetailContent({ step, showSensitiveFields = false, showVariantCheck = false, assigneeLookup = {}, entitySuggestions }) {
    const { t } = useTranslation();
    // The MAP, not the array, is threaded to every sub-section below, so each
    // ref lookup is O(1). The union rule itself lives in the hook.
    const suggestionInfoById = useStepSuggestionInfoMap(step, entitySuggestions);
    const isTask = step.actionType === "task";
    // recordedBy is backend-populated (deletedAt stripped) → lookup check by id.
    // It is null when its user was hard-deleted (dangling ref) — fall back to
    // the unknown-user label and skip the tombstone lookup.
    const isRecordedByDeleted = step.recordedBy ? isUserDeletedInLookup(step.recordedBy._id, assigneeLookup) : false;
    const recordedByName = step.recordedBy ? /*#__PURE__*/ _jsx(UserName, {
        name: `${step.recordedBy.firstName} ${step.recordedBy.lastName}`,
        user: assigneeLookup.users?.get(step.recordedBy._id) ?? step.recordedBy
    }) : t("viralRecording.labels.unknownUser");
    const taskDescription = step.task?.taskDescription || "";
    const assignedTo = resolveAssignedToDisplay(step, t, assigneeLookup, suggestionInfoById);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex gap-4",
                children: [
                    assignedTo && /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-0 flex-1",
                        children: [
                            /*#__PURE__*/ _jsxs("label", {
                                className: "flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1",
                                children: [
                                    /*#__PURE__*/ _jsx(User, {
                                        className: "w-3.5 h-3.5"
                                    }),
                                    t("processFlow.assignedTo")
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: cn("text-sm text-foreground bg-surface-subtle px-2 py-1.5 rounded-sm", assignedTo.kind === "entity" && assignedTo.isDeleted && DELETED_ENTITY_CLASS),
                                children: assignedTo.kind === "proposed" ? /*#__PURE__*/ _jsx(ProposedEntityNameLabel, {
                                    name: assignedTo.label,
                                    isDeclined: assignedTo.isDeclined
                                }) : assignedTo.kind === "entity" ? /*#__PURE__*/ _jsx(UserName, {
                                    name: assignedTo.name,
                                    lifecycle: assignedTo.lifecycle
                                }) : assignedTo.label
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-0 flex-1",
                        children: [
                            /*#__PURE__*/ _jsxs("label", {
                                className: "flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1",
                                children: [
                                    /*#__PURE__*/ _jsx(User, {
                                        className: "w-3.5 h-3.5"
                                    }),
                                    t("processFlow.recordedBy")
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: cn("text-sm text-foreground bg-surface-subtle px-2 py-1.5 rounded-sm", isRecordedByDeleted && DELETED_ENTITY_CLASS),
                                children: recordedByName
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(InputsList, {
                inputs: step.inputs,
                t: t
            }),
            isTask && taskDescription && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsxs("label", {
                        className: "flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1",
                        children: [
                            /*#__PURE__*/ _jsx(STEP_FIELD_ICONS.taskDescription, {
                                className: "w-3.5 h-3.5"
                            }),
                            t("processFlow.taskDescription")
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "text-sm text-foreground bg-surface-subtle px-2 py-1.5 rounded-sm",
                        children: taskDescription
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(HelpersSection, {
                helpers: step.helpers,
                t: t,
                lookup: assigneeLookup,
                suggestionInfoById: suggestionInfoById
            }),
            isTask && step.task?.output && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsxs("label", {
                        className: "flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1",
                        children: [
                            /*#__PURE__*/ _jsx(STEP_FIELD_ICONS.output, {
                                className: "w-3.5 h-3.5"
                            }),
                            t("processFlow.output")
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "text-sm text-foreground bg-surface-subtle px-2 py-1.5 rounded-sm",
                        children: step.task.output
                    })
                ]
            }),
            isTask && /*#__PURE__*/ _jsx(SuccessorsSection, {
                successors: step.task?.successors,
                t: t,
                lookup: assigneeLookup,
                suggestionInfoById: suggestionInfoById
            }),
            hasDecisionRules(step) && /*#__PURE__*/ _jsx(DecisionRulesSection, {
                rules: step.rules ?? [],
                t: t,
                assigneeLookup: assigneeLookup,
                suggestionInfoById: suggestionInfoById
            }),
            /*#__PURE__*/ _jsx(WhoElseDoesThisSection, {
                whoElseDoesThis: step.whoElseDoesThis,
                t: t,
                lookup: assigneeLookup,
                suggestionInfoById: suggestionInfoById
            }),
            showSensitiveFields && /*#__PURE__*/ _jsx(SurveyFeedbackSection, {
                surveys: step.surveys,
                t: t
            }),
            showVariantCheck && step.variantCheck && /*#__PURE__*/ _jsx(VariantCheckSection, {
                variantCheck: step.variantCheck,
                t: t
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "pt-3 border-t border-line text-xs text-muted-foreground space-y-1",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            t("processFlow.createdAt"),
                            ": ",
                            formatDateTime(step.createdAt)
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            t("processFlow.updatedAt"),
                            ": ",
                            formatDateTime(step.updatedAt)
                        ]
                    }),
                    step.recordingCompletedAt && /*#__PURE__*/ _jsxs("div", {
                        children: [
                            t("processFlow.completedAt"),
                            ":",
                            " ",
                            formatDateTime(new Date(step.recordingCompletedAt))
                        ]
                    })
                ]
            })
        ]
    });
}
export default function ProcessFlowStepDetailSidebar({ step, onClose, showSensitiveFields = false, showVariantCheck = false, assigneeLookup, entitySuggestions }) {
    return /*#__PURE__*/ _jsxs(StepDetailPanel, {
        children: [
            /*#__PURE__*/ _jsx(StepDetailHeader, {
                step: step,
                onClose: onClose,
                stepNumber: step.stepNumber
            }),
            /*#__PURE__*/ _jsx(StepDetailBody, {
                children: /*#__PURE__*/ _jsx(ProcessFlowStepDetailContent, {
                    step: step,
                    showSensitiveFields: showSensitiveFields,
                    showVariantCheck: showVariantCheck,
                    assigneeLookup: assigneeLookup,
                    entitySuggestions: entitySuggestions
                })
            })
        ]
    });
}

//# sourceMappingURL=ProcessFlowStepDetailSidebar.js.map